import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, inject, input, viewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { disableFormControlsBasedOnStatus, SessionQuery, SessionStore } from '../../../../core/state';
import {
  Cmg1003ApplicationInfoQuery,
  Cmg1003ApplicationInfoStore,
  Cmg1003BorrowerQuery,
  Cmg1003BorrowerStore,
  Cmg1003StoreQueryService,
} from '../../../../loan-state';
import { resetValidations, canUseUnsavedChanges, getUniqueId } from '../../../utils';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map, pairwise, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BorrowerModel } from '../../../../cmg-1003/models/borrower.model';
import { Cmg1003SectionTypes } from '../../../../cmg-1003/models/section-types.enum';
import { CmgUiUtilities } from '../../../../cmg-1003/components/common/cmg-ui-utils';
import { Router } from '@angular/router';
import { Cmg1003SubSectionTypes } from '../../../../cmg-1003/models/sub-section-types.enum';
import { LoanSummaryComponentActions } from 'apps/cmg-clear2-ui/src/app/loan-summary/components/common/loan-summary-component-actions';
import { InputText } from 'primeng/inputtext';
import { CmgFormControlDomDirective } from '../../../directives/cmg-form-control-dom.directive';

import { DatePicker } from 'primeng/datepicker';
import { CmgPrimeNgDatePickerMaskDirective } from '../../../directives/cmg-datepicker-mask.directive';
import { QaSlugPipe } from '../../../pipes/qa-slug.pipe';

@Component({
  selector: 'cmg-credit-info',
  templateUrl: './credit-info.component.html',
  styleUrls: ['./credit-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: LoanSummaryComponentActions, useExisting: CreditInfoComponent }],
  imports: [ReactiveFormsModule, InputText, CmgFormControlDomDirective, DatePicker, CmgPrimeNgDatePickerMaskDirective, QaSlugPipe],
})
export class CreditInfoComponent implements OnInit, OnDestroy, LoanSummaryComponentActions {
  private readonly _formBuilder = inject(UntypedFormBuilder);
  private readonly _borrowerQuery = inject(Cmg1003BorrowerQuery);
  private readonly _borrowerStore = inject(Cmg1003BorrowerStore);
  private readonly _applicationStore = inject(Cmg1003ApplicationInfoStore);
  private readonly _applicationQuery = inject(Cmg1003ApplicationInfoQuery);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _router = inject(Router);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);

  readonly focusedElement = viewChild('focusedElement', { read: ElementRef });

  creditInfoForm: UntypedFormGroup;

  @Input()
  borrower: BorrowerModel;

  /** For QA. The automation ID prefix, formatted as "borrowers-[index]-". */
  readonly autoIdPrefix = input<string>(undefined);

  @Input()
  set hasAdditionalBorrower(hasAdditionalBorrower: boolean) {
    this._hasAdditionalBorrower = hasAdditionalBorrower;
    this.allowAdditionalBorrower = !hasAdditionalBorrower;
    this.onAdditionalBorrowerChanged();
  }

  locked = false;
  isSoftLockedStatus = true;
  forceDisableOnLockKeys = ['equifaxScore', 'transUnionScore', 'experianScore', 'creditScoreMedian'];

  _hasAdditionalBorrower: boolean;
  isAdditionalBorrowerPresent = false;
  allowAdditionalBorrower = false;
  formChangesSub: Subscription;
  ngRequiredValidator = Validators.required;
  creditUrl = null;

  /**
   * This subscription should only be managed within `_updateBorrower()`.
   * Internally handles this.borrowerForm.valueChanges, but will be separate from this.formChangesSub.
   */
  updateBorrowerSubscription: Subscription;

  uniqueId = getUniqueId();
  ageFilter = /^[0-9, ]*$/;

  private _destroyed$ = new Subject();

  get isBorrowerEnabled() {
    const enabled =
      this.isSoftLockedStatus && (this.borrower?.isPrimaryBorrower || (!this.borrower?.isPrimaryBorrower && this.allowAdditionalBorrower));
    return enabled;
  }

  getSectionType() {
    return null;
  }

  validate(): void {
    CmgUiUtilities.markFormAsDirty(this.creditInfoForm);
    CmgUiUtilities.markFormAsTouched(this.creditInfoForm);

    if (this.creditInfoForm.invalid) {
      const errorPrefix = CmgUiUtilities.getBorrowerNameErrorPrefix(
        this.borrower?.isPrimaryBorrower,
        this.borrower?.firstName,
        this.borrower?.lastName,
        this.borrower?.suffix,
      );

      const controlsWithErrors = CmgUiUtilities.getControlsWithErrors(this.creditInfoForm);
      const invalidControls = CmgUiUtilities.getInvalidControlModels(controlsWithErrors);

      invalidControls.forEach((t) => {
        switch (t.formControlName) {
          case 'creditScoreMedian':
            if (t.errors.required) {
              t.invalidMessage = `${errorPrefix} Credit must be run on all applications`;
              t.action = () => {
                this._sessionQuery
                  .select((t) => t.fileDataId)
                  .pipe(take(1))
                  .subscribe((fileDataId) => {
                    this._router.navigate([`urla2/${fileDataId}/credit`]);
                  });
              };
            }
            break;
          default:
            break;
        }
      });

      const subSection = this.borrower.isPrimaryBorrower
        ? Cmg1003SubSectionTypes.primaryBorrowerDetails
        : Cmg1003SubSectionTypes.additionalBorrowerDetails;
      this._sessionStore.setValidationError(Cmg1003SectionTypes.borrowerInformation, invalidControls, subSection);

      // This ensures validation styling is applied after everything is checked.
      this._cdRef.detectChanges();
    }
  }

  resetValidations(): void {
    resetValidations(this.creditInfoForm);

    setTimeout(() => {
      this._cdRef.detectChanges();
    }, 100);
  }

  ngOnInit(): void {
    this.isAdditionalBorrowerPresent = this._applicationQuery.isAdditionalBorrowerPresent(this.borrower.applicationId);
    this.creditInfoForm = this._formBuilder.group(
      {
        creditScoreMedian: new FormControl({ value: null }, { validators: Validators.required }),
        experianScore: [{ value: '', disabled: true }],
        transUnionScore: [{ value: '', disabled: true }],
        equifaxScore: [{ value: '', disabled: true }],
        mobileEConsent: [{ value: '', disabled: true }],
        mobileCreditAuthorization: [{ value: '', disabled: true }],
      },
      { updateOn: 'blur' },
    );
    this._updateBorrower();
    this._awaitAutoRecoveryDecision();

    this._setupContinuousValidation();

    this._storeQuery
      .statusRequiresFullDisabling$()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((locked) => {
        this.locked = locked;
        this._checkToDisableForm();
      });

    this._storeQuery
      .statusRequiresDisabling$()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((locked) => {
        this.isSoftLockedStatus = !locked;
      });

    if (!this.isAdditionalBorrowerPresent && !this.borrower?.isPrimaryBorrower) {
      this.allowAdditionalBorrower = true;
      this.onAdditionalBorrowerChanged();
    }
  }

  private _setupContinuousValidation() {
    this._sessionQuery
      .select((s) => s.all1003Loaded)
      .pipe(
        filter((all1003Loaded) => all1003Loaded === true),
        debounceTime(500),
        take(1),
      )
      .subscribe(() => {
        this.creditInfoForm.statusChanges.pipe(debounceTime(0), takeUntil(this._destroyed$)).subscribe(() => {
          this._sessionStore.triggerRevalidation();
        });
      });
  }

  ngOnDestroy(): void {
    this._destroyed$.next(undefined);
    this._destroyed$.complete();
  }

  onAdditionalBorrowerChanged() {
    this.allowAdditionalBorrower = !this.allowAdditionalBorrower;
    this._applicationStore.setAdditionalBorrowerChecked(this.borrower.applicationId, this.allowAdditionalBorrower);
    if (this.allowAdditionalBorrower) {
      if (this.creditInfoForm) {
        this.creditInfoForm.enable({ emitEvent: false });
        // these remain disabled always
        this.creditInfoForm.get('equifaxScore').disable({ emitEvent: false });
        this.creditInfoForm.get('transUnionScore').disable({ emitEvent: false });
        this.creditInfoForm.get('experianScore').disable({ emitEvent: false });
        this.creditInfoForm.get('mobileEConsent').disable({ emitEvent: false });
        this.creditInfoForm.get('mobileCreditAuthorization').disable({ emitEvent: false });
        this.formChangesSub = this.creditInfoForm.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe(this._onBorrowerFormChanges);
      } else {
        if (this.formChangesSub && !this.formChangesSub.closed) {
          this.formChangesSub.unsubscribe();
        }
        if (this.creditInfoForm) {
          this.creditInfoForm.disable();
        }
      }
    }
  }

  private _checkToDisableForm() {
    // TODO - pass formDisabled into disableFormControlsBasedOnStatus as optional parameter so the statusRequiresFullDisabling check
    // does not run twice. We already know the form needs to be disabled here.
    if (this.locked || (!this.borrower.isPrimaryBorrower && !this.isAdditionalBorrowerPresent)) {
      disableFormControlsBasedOnStatus([this.creditInfoForm], this._storeQuery, this.forceDisableOnLockKeys, null, null, this.locked);
      return true;
    }

    return false;
  }

  private readonly _onBorrowerFormChanges = (formValues: UntypedFormGroup) => {
    const updatedBorrower = { ...this.borrower, ...formValues };

    this._borrowerStore.update(this.borrower.borrowerId, updatedBorrower);
  };

  private _updateBorrower() {
    if (!this.creditInfoForm) {
      return;
    }

    // Ensures infinite loop (from valueChanges) doesn't occur if borrower was updated by auto-recovery.
    this.updateBorrowerSubscription?.unsubscribe();

    this._sessionQuery
      .select((t) => t.fileDataId)
      .pipe(take(1))
      .subscribe((fileDataId) => (this.creditUrl = `/${fileDataId}/credit`));
    const dontEmitEvent = { emitEvent: false };
    this.creditInfoForm.get('creditScoreMedian').setValue(this.borrower.creditScores?.creditScoreMedian, dontEmitEvent);
    this.creditInfoForm.get('experianScore').setValue(this.borrower.creditScores?.experianScore, dontEmitEvent);
    this.creditInfoForm.get('transUnionScore').setValue(this.borrower.creditScores?.transUnionScore, dontEmitEvent);
    this.creditInfoForm.get('equifaxScore').setValue(this.borrower.creditScores?.equifaxScore, dontEmitEvent);
    this.creditInfoForm
      .get('mobileEConsent')
      .setValue(this.borrower.mobileEConsent === null ? null : new Date(this.borrower.mobileEConsent), dontEmitEvent);
    this.creditInfoForm
      .get('mobileCreditAuthorization')
      .setValue(this.borrower.mobileCreditAuthorization === null ? null : new Date(this.borrower.mobileCreditAuthorization), dontEmitEvent);

    const formDisabled = this._checkToDisableForm();

    if (!formDisabled) {
      this.updateBorrowerSubscription = this.creditInfoForm.valueChanges
        .pipe(
          startWith(this.creditInfoForm.value),
          pairwise(),
          map(([oldValues, newValues]) => {
            const key = Object.keys(newValues).find((k) => newValues[k] !== oldValues[k]);
            return {
              key: key,
              oldValue: oldValues[key],
              newValue: newValues[key],
            };
          }),
          takeUntil(this._destroyed$),
        )
        .subscribe((changeObj) => {
          // all of this to work around reverting to last valid value & unnecessary updates sent by pInputMask when data is invalid.
          const updateStore = true;
          if (changeObj.key) {
            if (updateStore) {
              this._onBorrowerFormChanges(this.creditInfoForm.value);
            } else {
              this.creditInfoForm.get(changeObj.key).setValue(changeObj.oldValue, { emitEvent: false, onlySelf: true });
              this.creditInfoForm.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            }
          }
        });
      //  Focus on primary borrower form element
      if (this.borrower.isPrimaryBorrower) {
        //  setTimeout else focusedElement is undefined
        setTimeout(() => {
          this.focusedElement()?.nativeElement.focus({ preventScroll: true });
        });
      }
    }

    if (!this.borrower.isPrimaryBorrower && !CmgUiUtilities.isIdTemporary(this.borrower.borrowerId)) {
      this.allowAdditionalBorrower = true;
    }
  }

  /**
   * [Auto-Recovery Flow]:
   * 1) Wait for user's decision on auto-recovery modal.
   * 2) User selects "Unsaved Changes" on auto-recovery modal.
   * 3) Borrower store gets updated, via _update1003ForAutoRecovery() in cmg-1003.service.ts.
   * 4) Observable fires (ONCE) to refresh Borrower using latest data.
   */
  private _awaitAutoRecoveryDecision() {
    combineLatest([this._sessionQuery.select((session) => session.needsAutoRecoveryDecision), this._borrowerQuery.dirtyChecker2.isDirty$])
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        filter(([needsDecision, isDirty]) => canUseUnsavedChanges(this._sessionStore.getValue(), isDirty)),
        take(1),
        switchMap(() =>
          combineLatest([
            this._borrowerQuery.selectEntity(this.borrower?.borrowerId),
            this._applicationQuery.selectEntity(this.borrower?.applicationId),
          ]).pipe(
            take(1),
            tap(([borrower, application]) => {
              if (this.isAdditionalBorrowerPresent !== application.additionalBorrowerChecked) {
                this.isAdditionalBorrowerPresent = application.additionalBorrowerChecked;
                this.onAdditionalBorrowerChanged();
              }

              this.borrower = borrower;
              this._updateBorrower();
            }),
          ),
        ),
      )
      .subscribe();
  }
}
